import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import {
  addDecimals,
  compareDecimalStrings,
  isDecimalString,
  MONEY_INPUT_PATTERN,
} from "@/shared/utils/decimal";

import { isCryptoCurrency } from "../constants/walletMethods.constants";
import { CURRENCY_TO_DISPERSION_CODE, CURRENCY_TO_FLAG_CODE } from "../constants/wallets.constants";
import type {
  RuntimeWallet,
  RuntimeWalletCurrencySummary,
  RuntimeWalletSort,
  RuntimeWalletStatus,
} from "../types/payments-wallets.types";

/** Code for `react-world-flags`, or null for crypto and currencies with no single country. */
export function walletFlagCode(currency: string): string | null {
  const code = currency.toUpperCase();
  if (isCryptoCurrency(code)) return null;
  return CURRENCY_TO_FLAG_CODE[code] ?? CURRENCY_TO_DISPERSION_CODE[code] ?? null;
}

/**
 * Decimals for an overview amount: the currency's settlement precision, capped at 2. Totals in the millions
 * do not need the 6 decimals USDT settles in, and COP shows none.
 */
export function overviewFractionDigits(currency: string): number {
  return Math.min(minorUnitsFor(currency), 2);
}

/** What Coloca must back for one wallet: available + blocked. Exact, in the 8-dp string scale. */
export function walletHeld(wallet: Pick<RuntimeWallet, "balance_available" | "balance_blocked">): string {
  return addDecimals(wallet.balance_available, wallet.balance_blocked);
}

/** Whether a currency has anything to back: available or blocked above zero. Pending alone does not count. */
export function hasHeld(row: Pick<RuntimeWalletCurrencySummary, "held">): boolean {
  return isDecimalString(row.held) && compareDecimalStrings(row.held, "0") > 0;
}

/**
 * Currencies holding money first, then by how many wallets they have, then by code. Amounts of different
 * currencies are never compared, so the order cannot be "largest first".
 */
export function orderCurrencySummaries(
  currencies: RuntimeWalletCurrencySummary[],
): RuntimeWalletCurrencySummary[] {
  const holds = (row: RuntimeWalletCurrencySummary) => (hasHeld(row) ? 1 : 0);
  return [...currencies].sort(
    (a, b) => holds(b) - holds(a) || b.wallets - a.wallets || a.currency.localeCompare(b.currency),
  );
}

/**
 * Share of `part` in `whole`, as a fraction for `Intl.NumberFormat` percent style. Display only — the one
 * place this module goes through `Number`. Null when the whole is zero or either side is not a decimal.
 */
export function shareOf(part: string, whole: string | null | undefined): number | null {
  if (!whole || !isDecimalString(part) || !isDecimalString(whole)) return null;
  const denominator = Number(whole);
  if (!(denominator > 0)) return null;
  return Math.min(1, Math.max(0, Number(part) / denominator));
}

/** The overview's filters. Amounts are decimal strings; a range only applies with a currency. */
export interface WalletsOverviewFilters {
  search: string;
  currency: string | null;
  status: RuntimeWalletStatus | null;
  /** Only wallets holding anything. On by default: the page is about the money held. */
  withBalance: boolean;
  /** Coloca's own root organization holds Coloca's money, not a client's, so it is left out by default. */
  includeColoca: boolean;
  /** Bounds on `held`. */
  minHeld: string;
  maxHeld: string;
  sort: RuntimeWalletSort;
}

const SORTS: ReadonlySet<string> = new Set<RuntimeWalletSort>([
  "held_desc",
  "held_asc",
  "available_desc",
  "pending_desc",
  "last_movement_desc",
  "created_desc",
]);
const STATUSES: ReadonlySet<string> = new Set<RuntimeWalletStatus>(["active", "frozen", "closed"]);
const CURRENCY_PATTERN = /^[A-Z]{3,8}$/;

export const DEFAULT_OVERVIEW_SORT: RuntimeWalletSort = "held_desc";

/**
 * Reads `?q&currency&status&funded&coloca&min&max&sort&page`. A malformed value is dropped rather than sent,
 * and a range without a currency is dropped too: the backend refuses it, since amounts in different
 * currencies cannot be compared.
 */
export function readWalletsOverviewUrl(params: URLSearchParams): {
  filters: WalletsOverviewFilters;
  page: number;
} {
  const rawCurrency = params.get("currency")?.trim().toUpperCase() ?? "";
  const currency = CURRENCY_PATTERN.test(rawCurrency) ? rawCurrency : null;
  const amount = (key: string) => {
    const raw = params.get(key)?.trim() ?? "";
    return currency && MONEY_INPUT_PATTERN.test(raw) ? raw : "";
  };
  const sort = params.get("sort") ?? "";
  const status = params.get("status") ?? "";
  const page = Number(params.get("page"));
  return {
    filters: {
      search: params.get("q")?.trim() ?? "",
      currency,
      status: STATUSES.has(status) ? (status as RuntimeWalletStatus) : null,
      withBalance: params.get("funded") !== "0",
      includeColoca: params.get("coloca") === "1",
      minHeld: amount("min"),
      maxHeld: amount("max"),
      sort: SORTS.has(sort) ? (sort as RuntimeWalletSort) : DEFAULT_OVERVIEW_SORT,
    },
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}
